import { Dispatch, SetStateAction } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import CustomNav from "./CustomNav";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/react";
import { CalendarIcon, ChevronDownIcon } from "@heroicons/react/24/solid";

interface ICalenderBaseProps {
  month?: Date;
  onMonthChange?: Dispatch<SetStateAction<Date | undefined>>;
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  close?: () => void;
  fromDate?: Date;
  toDate?: Date;
}

const CalenderBase2 = ({
  month,
  onMonthChange,
  selected,
  onSelect,
  fromDate,
  toDate,
}: ICalenderBaseProps) => {
  const defaultClassNames = getDefaultClassNames();
  const hasSelectableDates =
    !fromDate || !toDate || fromDate.getTime() <= toDate.getTime();
  return (
    <div className="py-1">
      {hasSelectableDates ? (
        <>
          <DayPicker
            month={month}
            onMonthChange={onMonthChange}
            mode="single"
            selected={selected}
            onSelect={onSelect}
            startMonth={fromDate}
            endMonth={toDate ?? new Date(2100, 11)}
            disabled={[
              ...(fromDate ? [{ before: fromDate }] : []),
              ...(toDate ? [{ after: toDate }] : []),
            ]}
            components={{
              Nav: (props) => (
                <CustomNav {...props} currentMonth={month as Date} />
              ),
              Dropdown: (props) => {
                const { value, options, onChange } = props;
                const selectedLabel = options?.find(
                  (o) => o.value === value
                )?.label;

                return (
                  <Listbox
                    value={value}
                    onChange={(v) =>
                      onChange?.({ target: { value: v } } as any)
                    }
                  >
                    <ListboxButton
                      className="w-full text-body text-sm font-semibold rounded-md border border-lightGray p-1 
                                          flex gap-2 justify-between items-center"
                    >
                      {selectedLabel}
                      <ChevronDownIcon className="size-4 " />
                    </ListboxButton>
                    <ListboxOptions
                      anchor="bottom"
                      className="z-50 rounded-md border border-lightGray bg-white shadow-lg !max-h-44 overflow-auto 
                             divide-y divide-lightGray [--anchor-gap:4px] w-24"
                    >
                      {options?.map((option) => (
                        <ListboxOption
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          className="px-2 py-1 cursor-pointer data-[focus]:bg-primary/10 data-[disabled]:!text-extraLightGray 
                                 data-[disabled]:cursor-not-allowed text-sm font-semibold text-body"
                        >
                          {option.label}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                );
              },
            }}
            captionLayout="dropdown"
            classNames={{
              root: `${defaultClassNames.root} h-full w-full`,
              month_grid: "w-full",
              month: "px-2",
              dropdowns: "flex w-full gap-2",
              dropdown: "flex-1 min-w-0",
              selected: "bg-primary/80 rounded-md text-white",
              day: "font-semibold text-subHeading text-center p-1.5",
              disabled: "!text-extraLightGray",
              weekdays: "text-darkGray text-sm font-light",
              week: "text-sm text-subHeading",
            }}
          />
          <div className="flex justify-end border-lightGray px-3">
            <button
              type="button"
              onClick={() => onSelect(undefined)}
              disabled={!selected}
              className="text-xs font-semibold text-error disabled:text-extraLightGray disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </>
      ) : (
        <div className="text-center grid gap-2 p-5 h-full w-44">
          <div className="flex justify-center">
            <CalendarIcon className="size-6 text-gray-300" />
          </div>
          <p className="font-semibold text-body text-sm">No dates available</p>
          <p className="text-xs text-darkGray">
            There are no selectable dates for the current criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default CalenderBase2;
